import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch, useSelector } from "src/store";

import { updateSearchQuery } from "@dashboard/devices/store";
import { Group } from "@dashboard/devices/types/index";
import { setColumnVisibility } from "@dashboard/devices/utils/columnVisibility";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { expandGroupIdsWithDescendants, getGroupsForAggregation } from "@dashboard/groups/utils";
import { calculateBarValue } from "@dashboard/overview/components/shared";
import { Theme } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ElasticService from "@services/elasticService";
import { CustomTileText } from "@shared/CustomTile";
import { ProgressBar } from "@shared/ProgressBar";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { TextLink } from "@shared/TextLink";

import { useStatusCardStyles } from "../StatusCard.styles";

const aggregateOnlineOfflineCountsFromGroups = (groups: Group[], groupIds: number[]) =>
    getGroupsForAggregation(groupIds, groups).reduce(
        (acc, g) => ({
            online: acc.online + (g.statistics?.connectionStatus?.online ?? 0),
            offline: acc.offline + (g.statistics?.connectionStatus?.offline ?? 0),
        }),
        { online: 0, offline: 0 },
    );

const getBarColor = (totalDevices: number, onlineDevices: number, theme: Theme) => {
    if (onlineDevices === totalDevices) {
        return theme.palette.emteria.success;
    }
    if (onlineDevices === 0) {
        return theme.palette.emteria.error;
    }
    return theme.palette.emteria.warning;
};

type Props = {
    groupIds: number[];
};

const OnlineStatusCard = ({ groupIds }: Props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation(["pages", "common"]);
    const { classes: statusCardClasses } = useStatusCardStyles();
    const theme = useTheme();

    const allGroups = useSelector(selectGroups);

    const connectionCounts = aggregateOnlineOfflineCountsFromGroups(allGroups, groupIds);
    const onlineDevicesCount = connectionCounts.online;
    const offlineDevicesCount = connectionCounts.offline;
    const totalDevices = onlineDevicesCount + offlineDevicesCount;
    const offlineDeviceCount = totalDevices - onlineDevicesCount;

    const barValue = calculateBarValue(offlineDeviceCount, totalDevices);
    const barColor = getBarColor(totalDevices, onlineDevicesCount, theme);

    const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        const filters = [
            {
                id: "onlineStatus",
                value: [onlineDevicesCount < totalDevices ? "Offline" : "Online"],
            },
            { id: "licenseStatus", value: ["Used"] },
        ];

        if (groupIds.length !== 0 && groupIds.length !== allGroups.length) {
            const expandedGroupIds = expandGroupIdsWithDescendants(groupIds, allGroups);
            filters.push({
                id: "groupName",
                value: expandedGroupIds.map((id) => id.toString()),
            });
        }

        UserSettingsService.setWorkspaceValue("devicePage.columnFilters", filters);
        setColumnVisibility("onlineStatus");
        setColumnVisibility("licenseStatus");
        setColumnVisibility("groupName");
        dispatch(updateSearchQuery(""));
        void navigate("/dashboard/devices");

        const text = e.currentTarget.textContent;
        if (text) {
            ElasticService.createTransaction("overviewPageLinkClick", {
                tab: "connection-status",
                text,
            });
        }
    };

    return (
        <SectionPanel title={t("pages:overviewPage.cards.onlineStatus.title")}>
            {!totalDevices ? <CustomTileText>{t("common:table.noDevices")}</CustomTileText> : null}
            {totalDevices && onlineDevicesCount === totalDevices ? (
                <CustomTileText>{t("pages:overviewPage.cards.onlineStatus.allDevicesOnline")}</CustomTileText>
            ) : null}
            {totalDevices && onlineDevicesCount !== totalDevices ? (
                <TextLink onClick={handleOnClick} tooltip={t("common:tooltip.showDevices", { count: offlineDeviceCount })}>
                    {t("pages:overviewPage.cards.onlineStatus.offlineDevices", { count: offlineDeviceCount })}
                </TextLink>
            ) : null}
            <ProgressBar
                className={statusCardClasses.progressBar}
                progress={totalDevices ? barValue : 0}
                color={barColor}
                hideLabel={!totalDevices}
            />
        </SectionPanel>
    );
};

export default OnlineStatusCard;
